import {ErrorType} from '@dvrd/dvr-controls';

import React, {ReactNode, useEffect, useState} from 'react';

import {portalItemIsGroup, PortalSelectItem, PortalSelectOption, PortalSelectValue} from '../../../util/types';

import PortalSelect from './portalSelect';

interface Props<VT> {
    /** Called when the selection changes. Receives the changed value (or null for bulk operations),
     * whether it was selected, and the full array of selected values when in multiple mode. */
    onChange: (value: VT | null, selected: boolean, values?: Array<VT>) => void;
    /** Optional callback that gets called when the items are closed. Useful when something needs to be loaded only
     * after closing the items. */
    onCloseItems?: VoidFunction;
    /** Custom parser for the selected value. Receives the selected value(s) and returns a string representation. */
    parseSelectValue?: (value: PortalSelectValue<VT>) => string;
    /** Disables the select, preventing user interaction. Defaults to false. */
    disabled?: boolean;
    /** The list of selectable items, which can include grouped items. */
    items: Array<PortalSelectItem<VT>>;
    /** The controlled value. Use an array when `multiple` is true. Either `value` or `defaultValue` must be provided. */
    value?: VT | Array<VT>;
    /** The initial uncontrolled value. Use an array when `multiple` is true. Either `value` or `defaultValue` must be provided. */
    defaultValue?: VT | Array<VT>;
    /** Placeholder text shown when no value is selected. */
    placeholder?: string;
    /** Label displayed for the currently selected value. */
    selectLabel?: string;
    /** DOM element or CSS selector string for the portal target where the dropdown is rendered. */
    portalTarget?: Element | string;
    /** Label displayed above the select input. */
    label?: ReactNode;
    /** Error state to display on the select input. */
    error?: ErrorType;
    /** Additional CSS class name(s) applied to the root element. */
    className?: string;
    /** Additional CSS class name(s) applied to the items element. */
    itemsClassName?: string;
    /** Enables multi-select mode, allowing multiple items to be selected. Defaults to false. */
    multiple?: boolean;
    /** Position of the dropdown relative to the select input. Defaults to 'bottom'. */
    itemsPosition?: 'top' | 'bottom';
    /** Height of the dropdown list. Accepts a number (px) or CSS string value. */
    itemsHeight?: number | string;
    /** Border shape of the select input. Defaults to 'square'. */
    shape?: 'square' | 'rounded';
    /** Tinted fill + solid blue border + rounded-rectangle corners. Shared
     * design-system option, matching BluemateButton's `outlined`. Defaults to false. */
    outlined?: boolean;
    /** Renders the trigger without its box shadow (e.g. inside dialogs). Defaults to false. */
    noShadow?: boolean;
    /** Unified form typography: 0.9rem/500 label, 1rem/500 value — so mixed
     * controls in one form read as one family. Defaults to false. */
    uniformFont?: boolean;
    /** Size variant of the select input. Defaults to 'medium'. */
    size?: 'small' | 'medium' | 'large';
    /** Bulk selection controls shown in the dropdown. Only applicable when `multiple` is true. */
    controls?: Array<'selectAll' | 'deselectAll'>;
    /** Option to disable automatically scrolling to the selected option. */
    disableAutoScroll?: boolean;
}

function validate<VT>(
    value: VT | Array<VT> | undefined,
    defaultValue: VT | Array<VT> | undefined,
    multiple: boolean | undefined
) {
    if (value === undefined && defaultValue === undefined)
        throw new TypeError('PortalSelect: value or defaultValue must be provided');
    if (value !== undefined)
        if (multiple && !Array.isArray(value))
            throw new TypeError('PortalSelect: multiple is only supported when value is an array');
    if (defaultValue !== undefined)
        if (multiple && !Array.isArray(defaultValue))
            throw new TypeError('PortalSelect: multiple is only supported when defaultValue is an array');
}

function getSelectableItems<VT>(items: Array<PortalSelectItem<VT>>): Array<PortalSelectOption<VT>> {
    return items.reduce((prev: Array<PortalSelectOption<VT>>, item: PortalSelectItem<VT>) => {
        if (portalItemIsGroup(item)) return prev.concat(getSelectableItems(item.items));
        else if (item.selectable === false) return prev;
        return prev.concat(item);
    }, []);
}

export default function PortalSelectController<VT = string>(props: Props<VT>) {
    const {
        disabled = false,
        items,
        placeholder,
        selectLabel,
        portalTarget,
        label,
        error,
        className,
        multiple = false,
        itemsPosition = 'bottom',
        itemsHeight,
        shape = 'square',
        size = 'medium',
        controls,
        itemsClassName,
        disableAutoScroll = false,
        outlined = false,
        noShadow = false,
        uniformFont = false,
        parseSelectValue,
        onCloseItems
    } = props;
    validate(props.value, props.defaultValue, multiple);
    const [value, setValue] = useState<VT | Array<VT>>((props.value ?? props.defaultValue) as VT);
    const [open, setOpen] = useState(false);

    // Sync internal state when controlled value changes externally
    useEffect(() => {
        if (props.value !== undefined) setValue(props.value as VT | Array<VT>);
    }, [props.value]);

    function onClose() {
        setOpen(false);
        onCloseItems?.();
    }

    function onToggleOpen() {
        if (disabled) return;
        const nextOpen = !open;
        setOpen(nextOpen);
        if (!nextOpen) onCloseItems?.();
    }

    function onSelectAll() {
        if (!multiple) return;
        const nextValue = getSelectableItems(items).map(item => item.value);
        setValue(nextValue);
        props.onChange(null, true, nextValue);
    }

    function onDeselectAll() {
        if (!multiple) return;
        setValue([]);
        props.onChange(null, false, []);
    }

    function onChange(value: VT) {
        return function () {
            if (multiple) onChangeMultiple(value);
            else {
                // Action items fire onChange but must not become the selected
                // value: the parent may reject the click's side effect (e.g. the
                // user cancels the add-account flow) while keeping the same
                // controlled value, which would otherwise leave the internal
                // state — and the displayed selection — stuck on this row.
                const isAction = getSelectableItems(items).some(item => item.value === value && item.action === true);
                if (!isAction) setValue(value);
                props.onChange(value, true);
                onClose();
            }
        };
    }

    function onChangeMultiple(_value: VT) {
        const nextValue = (value as Array<VT>).slice();
        let selected: boolean;
        if (nextValue.includes(_value)) {
            nextValue.splice(nextValue.indexOf(_value), 1);
            selected = false;
        } else {
            nextValue.push(_value);
            selected = true;
        }
        setValue(nextValue);
        props.onChange(_value, selected, nextValue);
    }

    return (
        <PortalSelect
            onChange={onChange}
            onToggleOpen={onToggleOpen}
            onClose={onClose}
            onSelectAll={onSelectAll}
            onDeselectAll={onDeselectAll}
            parseSelectValue={parseSelectValue}
            open={open}
            items={items}
            controls={controls}
            value={value}
            disabled={disabled}
            label={label}
            error={error}
            className={className}
            placeholder={placeholder}
            selectLabel={selectLabel}
            portalTarget={portalTarget}
            itemsPosition={itemsPosition}
            itemsHeight={itemsHeight}
            shape={shape}
            size={size}
            multiple={multiple}
            itemsClassName={itemsClassName}
            disableAutoScroll={disableAutoScroll}
            outlined={outlined}
            noShadow={noShadow}
            uniformFont={uniformFont}
        />
    );
}

PortalSelectController.displayName = 'PortalSelectController';
